import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth/AuthProvider';

/**
 * Gate for the whole portal. Two distinct refusals:
 *   anon                → send to sign-in.
 *   authed, no firmId   → signed in, but not an advisor. The API would 403 every
 *                         request anyway; say so plainly rather than rendering a
 *                         dashboard that fails to load. This is a COURTESY, not a
 *                         security control — the server is the authority.
 */
export function RequireAdvisor({ children }: { children: ReactNode }) {
  const { status, user } = useAuth();
  const location = useLocation();

  if (status === 'hydrating') {
    return (
      <div className="authSplash" role="status" aria-live="polite">
        <p className="la-label">Opening the vault</p>
      </div>
    );
  }

  if (status === 'anon' || user === null) {
    return <Navigate to="/signin" replace state={{ from: location }} />;
  }

  if (user.firmId === null) {
    return (
      <div className="authSplash">
        <h1>This account isn't an advisor</h1>
        <p>
          {user.email} is signed in, but isn't a member of an advisory firm. Ask your firm
          administrator to add you, then sign in again.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
